/**
 * Return the last element of an array, or undefined for an empty array
 */
export function lastElement(arr) {
    return arr.slice(-1)[0];
}
/**
 * Given an array of objects, return the object whose keyed value is the highest.
 */
export function maxByKey(data, key) {
    return data.reduce((prev, curr) => (prev[key] > curr[key] ? prev : curr));
}
/**
 * Given an array of objects, return the object whose keyed value is the lowest.
 */
export function minByKey(data, key) {
    return data.reduce((prev, curr) => (prev[key] < curr[key] ? prev : curr));
}
/**
 * Asserts that a value is a single value, not an array.
 */
export function isSingleValue(value) {
    return !Array.isArray(value);
}
export function sum(arr) {
    return arr.reduce((acc, val) => acc + val, 0);
}
export function average(arr) {
    return sum(arr) / arr.length;
}
/**
 * Extract the value at percentile `p` from an array of numbers.
 */
export function percentile(arr, p) {
    const sorted = arr.slice().sort((a, b) => a - b);
    const index = Math.min(sorted.length - 1, Math.floor((p / 100) * sorted.length));
    return Number(sorted[index]);
}
/**
 * Determine the percentile of a value within a range of values.
 */
export function percentileInRange(value, range) {
    const numericValues = range.filter((v) => !isNaN(Number(v)));
    const sorted = sort(numericValues);
    const min = sorted[0];
    const max = lastElement(sorted);
    if (min === max) {
        return 50;
    }
    return Math.min(100, Math.max(0, ((value - min) / (max - min)) * 100));
}
// Memoize the sort function to avoid re-sorting the same array multiple times
const sortMemo = new Map();
export function sort(arr) {
    const key = arr.join(",");
    if (sortMemo.has(key)) {
        return sortMemo.get(key);
    }
    const sorted = arr.slice().sort((a, b) => a - b);
    sortMemo.set(key, sorted);
    return sorted;
}
/**
 * Group an array of objects by one of their keys.
 *
 * @example
 * groupBy([
 *  { name: 'Banana', type: 'fruit', },
 *  { name: 'Carrot', type: 'vegetable', },
 *  { name: 'Apple', type: 'fruit', },
 * ], 'type')
 *
 * {
 *   fruit: [
 *     { name: 'Banana', type: 'fruit', },
 *     { name: 'Apple', type: 'fruit', },
 *   ],
 *   vegetable: [
 *     { name: 'Carrot', type: 'vegetable', },
 *   ]
 * }
 */
export function groupBy(key, array) {
    return array.reduce((acc, obj) => {
        const groupKey = String(obj[key]);
        if (!acc[groupKey]) {
            acc[groupKey] = [];
        }
        acc[groupKey].push(obj);
        return acc;
    }, {});
}
/**
 * Split an array into chunks of a specified size.
 */
export function chunkArray(arr, chunkSize) {
    const chunks = [];
    for (let i = 0; i < arr.length; i += chunkSize) {
        chunks.push(arr.slice(i, i + chunkSize));
    }
    return chunks;
}
